import { defineComponent, inject, computed } from 'vue';
import { useLocalizationSupport } from '../../composables/localization-support/index.js';
import { useStoreModule } from '../../composables/store-module/index.js';
import UiAssetClassBadge from '../ui-asset-class-badge/UiAssetClassBadge.vue.js';
import UiValueBar from '../ui-value-bar/UiValueBar.vue.js';
import UiCopyToClipboard from '../ui-copy-to-clipboard/UiCopyToClipboard.vue.js';
import UiValue from '../ui-value/UiValue.vue.js';
import UiRangeBar from '../ui-range-bar/UiRangeBar.vue.js';
import { marketSelectorFactory } from '../market-selector/setup/index.js';
import { companyLogoFactory } from '../company-logo/setup/index.js';
import HoistComponent from '../hoist-component/HoistComponent.vue.js';
import { generateElementId } from '../../helpers/generators.js';

var _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "FactsheetHeader",
  props: {
    locale: { type: null, required: true },
    id: { type: null, required: true },
    hasLogo: { type: Boolean, required: false, default: false },
    disableMarketSelector: { type: Boolean, required: false, default: false }
  },
  emits: ["store-error", "data-load", "exchange-change"],
  setup(__props, { emit: emits }) {
    const props = __props;
    const injectedStoreModule = inject("storeModule");
    const translator = inject("translator");
    const numberFormatter = inject("numberFormatter");
    const dateFormatter = inject("dateFormatter");
    const MarketSelector = marketSelectorFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().marketSelector : void 0
    });
    const CompanyLogo = companyLogoFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().companyLogo : void 0
    });
    const componentInputs = computed(() => ({
      id: props.id,
      language: props.locale
    }));
    const localizationSupport = useLocalizationSupport(props, {
      translator: inject("translator"),
      numberFormatter: inject("numberFormatter"),
      dateFormatter: inject("dateFormatter")
    });
    const { translate, textFormat, numberFormat, dateFormat } = localizationSupport;
    const storeModule = useStoreModule(
      { storeModule: injectedStoreModule },
      componentInputs,
      emits
    );
    const { storeData, isLoadingStoreData } = storeModule;
    const highLowBar = generateElementId("fds-factsheet-header-high-low-bar-");
    const maxPerformance = computed(() => {
      var _a, _b;
      return Math.max(...(_b = (_a = performanceBar.value) == null ? void 0 : _a.map((bar) => {
        var _a2;
        return Math.abs((_a2 = bar.value) != null ? _a2 : Number.NaN);
      })) != null ? _b : []);
    });
    const priceQualitySymbol = computed(() => {
      var _a;
      switch ((_a = storeData.value) == null ? void 0 : _a.quality) {
        case "RLT":
          return "R";
        case "DLY":
          return "D";
        case "EOD":
          return "E";
        default:
          return "";
      }
    });
    const performanceBar = computed(() => {
      var _a, _b;
      return (_b = (_a = storeData.value) == null ? void 0 : _a.performanceBar) != null ? _b : void 0;
    });
    const baseInfo = computed(() => {
      var _a, _b;
      return (_b = (_a = storeData.value) == null ? void 0 : _a.baseInfo) != null ? _b : void 0;
    });
    const trendIconClassModifier = computed(() => {
      var _a, _b;
      return `fds-ui-icon--${getIconClass((_b = (_a = storeData.value) == null ? void 0 : _a.performance) != null ? _b : Number.NaN)}`;
    });
    const assetClass = computed(() => {
      var _a, _b;
      return (_b = (_a = storeData.value) == null ? void 0 : _a.assetClass) != null ? _b : void 0;
    });
    const onExchangeChange = (market) => {
      if ((market == null ? void 0 : market.id) !== props.id) {
        emits("exchange-change", market);
      }
    };
    const getBaseInfoValue = (value) => {
      if (typeof value === "string") {
        return textFormat(value);
      }
      if (value && !isNaN(value)) {
        return numberFormat(value);
      }
      return textFormat(null);
    };
    const getIconClass = (value, base = 0) => {
      if (value < base) {
        return "trending-negative";
      } else if (value > base) {
        return "trending-positive";
      }
      return "neutral";
    };
    return { __sfc: true, injectedStoreModule, translator, numberFormatter, dateFormatter, MarketSelector, CompanyLogo, props, emits, componentInputs, localizationSupport, translate, textFormat, numberFormat, dateFormat, storeModule, storeData, isLoadingStoreData, highLowBar, maxPerformance, priceQualitySymbol, performanceBar, baseInfo, trendIconClassModifier, assetClass, onExchangeChange, getBaseInfoValue, getIconClass, UiAssetClassBadge, UiValueBar, UiCopyToClipboard, UiValue, UiRangeBar, HoistComponent };
  }
});

export { _sfc_main as default };
